import { useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { v4 } from "uuid";

import type { SupersetDashboard } from "@thalamos/aspire-common";

import { apiHooks } from "../../api.js";
import { routeFns } from "../../routePaths.js";
import type { MenuOptionsType } from "~/components/design-system/index.js";
import {
  Banner,
  BannerList,
  Button,
  ButtonIcon,
  MenuFlyout,
} from "~/components/design-system/index.js";

export interface SupersetDashboardsManagementPageInnerProps {
  dashboards: SupersetDashboard[];
  loading: boolean;
  error: Error | null;
  filterQuery: string;
  onFilterChange: (query: string) => void;
  page: number;
  numPages: number;
  onPageChange: (page: number) => void;
  onCreateClick: () => void;
  onEditClick: (dashboardId: string) => void;
}

function SupersetDashboardRowInner({
  dashboard,
  onEditClick,
}: {
  dashboard: SupersetDashboard;
  onEditClick: (dashboardId: string) => void;
}) {
  const cardRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const options: MenuOptionsType[] = [
    {
      name: "Edit",
      onClick: () => {
        onEditClick(dashboard.id);
      },
      icon: "edit",
      disabled: false,
    },
  ];

  return (
    <TableRow>
      <TableCell>{dashboard.displayName}</TableCell>
      <TableCell>{dashboard.dashboardId}</TableCell>
      <TableCell>{dashboard.embeddedUuid}</TableCell>
      <TableCell>
        <Box
          ref={cardRef}
          onClick={() => {
            setIsMenuOpen(true);
          }}
        >
          <MenuFlyout
            cardRef={cardRef}
            options={options}
            isOpen={isMenuOpen}
            onClose={() => {
              setIsMenuOpen(false);
            }}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
}

export function SupersetDashboardsManagementPageInner({
  dashboards,
  loading,
  error,
  filterQuery,
  onFilterChange,
  page,
  numPages,
  onPageChange,
  onCreateClick,
  onEditClick,
}: SupersetDashboardsManagementPageInnerProps) {
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          marginTop: "1em",
          marginBottom: "1em",
        }}
      >
        <TextField
          placeholder="Filter dashboards..."
          aria-label="Filter dashboards..."
          sx={{ width: "100%" }}
          value={filterQuery}
          onChange={(e) => {
            onFilterChange(e.target.value);
          }}
        />
        <Box sx={{ display: "flex", marginLeft: 2, marginBottom: 2 }}>
          <Button
            variant="contained"
            label="Create"
            endIcon={ButtonIcon.add}
            onClick={onCreateClick}
          />
        </Box>
      </Box>

      {dashboards.length === 0 && loading && (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Banner bannerType={BannerList.ERROR} title={error.message} />
      )}

      {!loading &&
        !error &&
        (dashboards.length > 0 ? (
          <Box>
            <Box
              sx={{ width: "100%", display: "flex", justifyContent: "center" }}
            >
              <button
                onClick={() => {
                  if (page > 0) onPageChange(page - 1);
                }}
                aria-label="Previous page"
              >
                &#60;
              </button>
              <Box sx={{ marginLeft: "1em", marginRight: "1em" }}>
                Page {page + 1} of {numPages}
              </Box>
              <button
                onClick={() => {
                  if (page < numPages - 1) onPageChange(page + 1);
                }}
                aria-label="Next page"
              >
                &#62;
              </button>
            </Box>
            <TableContainer>
              <Table size="small" aria-label="Superset dashboards table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Display Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Dashboard ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Embedded UUID
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboards.map((dashboard) => (
                    <SupersetDashboardRowInner
                      key={dashboard.id}
                      dashboard={dashboard}
                      onEditClick={onEditClick}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <p>No Superset dashboards found</p>
        ))}
    </Box>
  );
}

export function SupersetDashboardsManagementPage() {
  const [filterQuery, setFilterQuery] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const navigate = useNavigate();

  const [{ data: dashboards, loading, error }] =
    apiHooks.supersetDashboards.search(filterQuery, pageSize, page * pageSize);

  const results = dashboards?.results ?? [];
  const numPages =
    dashboards && dashboards.count ? Math.ceil(dashboards.count / pageSize) : 1;

  return (
    <SupersetDashboardsManagementPageInner
      dashboards={results}
      loading={loading}
      error={error ?? null}
      filterQuery={filterQuery}
      onFilterChange={(query) => {
        setFilterQuery(query);
        setPage(0);
      }}
      page={page}
      numPages={numPages}
      onPageChange={setPage}
      onCreateClick={() => {
        navigate(routeFns.supersetDashboardCreateEdit(v4()));
      }}
      onEditClick={(dashboardId) => {
        navigate(routeFns.supersetDashboardCreateEdit(dashboardId));
      }}
    />
  );
}
